import { getConnectionsProfiles } from '@/api/talkytimes/personalData';
import { getUserIsVip } from '@/api/talkytimes/message';
import dayjs from 'dayjs';

const state = {
  pendingRequests: [], // 正在请求的用户ID数组，避免重复请求
};

const mutations = {
  ADD_PENDING_REQUEST(state, userId) {
    if (!state.pendingRequests.includes(userId)) {
      state.pendingRequests.push(userId);
    }
  },

  REMOVE_PENDING_REQUEST(state, userId) {
    const index = state.pendingRequests.indexOf(userId);
    if (index > -1) {
      state.pendingRequests.splice(index, 1);
    }
  },

  CLEAR_PENDING_REQUESTS(state) {
    state.pendingRequests = [];
  },
};

const actions = {
  // 获取用户信息，支持单个或多个用户ID，每次都重新请求最新数据
  async getUserInfo({ state, commit }, userIds) {
    const ids = Array.isArray(userIds) ? userIds : [userIds];
    const result = {};
    const needFetchIds = [];

    // 只检查正在请求的状态，避免重复请求
    ids.forEach((id) => {
      if (!state.pendingRequests.includes(id)) {
        needFetchIds.push(id);
      }
    });

    // 如果有需要请求的用户信息
    if (needFetchIds.length > 0) {
      // 标记为正在请求
      needFetchIds.forEach((id) => {
        commit('ADD_PENDING_REQUEST', id);
      });

      try {
        // 将每个ID转换为数字格式
        const numericIds = needFetchIds.map((id) => Number(id));
        const isVipRes = await getUserIsVip(numericIds);
        const res = await getConnectionsProfiles({ ids: numericIds });
        if (
          res &&
          res.data &&
          res.data.profiles &&
          res.data.profiles.length > 0
        ) {
          // 调整数据结构
          res.data.profiles.forEach((profiles) => {
            let userData = {
              ...profiles,
              ...(profiles.personal || {}),
              ...(profiles.preferences || {}),
              ...(profiles.splits || {}),
            };
            delete userData.personal;
            delete userData.preferences;
            delete userData.splits;

            if (userData.last_visit) {
              // 返回的是字符串时间，强制加上8个小时
              userData.last_visit = dayjs(userData.last_visit)
                .add(8, 'hour')
                .format('YYYY-MM-DD HH:mm:ss');
            }

            // 合并VIP状态数据
            if (isVipRes && isVipRes[profiles.id]) {
              userData.isVip = isVipRes[profiles.id].isVip;
              userData.isUnmanagedVip = isVipRes[profiles.id].isUnmanagedVip;
            } else {
              // 如果没有VIP数据，设置默认值
              userData.isVip = false;
              userData.isUnmanagedVip = false;
            }
            // 直接添加到结果中，不存储缓存
            result[profiles.id] = userData;
          });
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      } finally {
        // 移除请求状态
        needFetchIds.forEach((id) => {
          commit('REMOVE_PENDING_REQUEST', id);
        });
      }
    }

    return result;
  },

  // 批量匹配聊天列表的用户信息，每次都重新请求最新数据
  async matchChatListUserInfo(
    { dispatch },
    { chatList, userIdField = 'idInterlocutor', platformUserIdField = 'idUser' }
  ) {
    if (!chatList || chatList.length === 0) {
      return chatList;
    }

    // 提取所有需要的用户ID（包括聊天对象和自己的ID）
    const interlocutorIds = chatList
      .map((chat) => chat[userIdField])
      .filter((id) => id);
    const platformUserIds = chatList
      .map((chat) => chat[platformUserIdField])
      .filter((id) => id);

    // 合并所有需要的用户ID，去重
    const allUserIds = [...new Set([...interlocutorIds, ...platformUserIds])];

    if (allUserIds.length === 0) {
      return chatList;
    }

    // 获取用户信息（每次都重新请求最新数据）
    const userInfoMap = await dispatch('getUserInfo', allUserIds);
    // 为聊天列表匹配用户信息
    return chatList.map((chat) => ({
      ...chat,
      userId: chat[userIdField], // 聊天对象的用户ID
      platformUserId: chat[platformUserIdField], // 自己的用户ID
      userDetail: userInfoMap[chat[userIdField]] || null, // 聊天对象的用户信息
      platformUserDetail: userInfoMap[chat[platformUserIdField]] || null, // 自己的用户信息
    }));
  },
};

const getters = {
  isPendingRequest: (state) => (userId) => {
    return state.pendingRequests.includes(userId);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
